import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {SchemaTag} from 'folder-items/FileConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {
  makeScheduledOnCell,
  makeTaskAssigneeCell,
  makeTaskStatusCell,
} from 'processes/tables/MakeProcessCellUtils';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  BulkFetchProcessTaskQuery,
  BulkFetchProcessTaskQueryVariables,
} from './__generated/BulkFetchProcessTaskQuery';
import {ProcessTaskStructuredTableRowMetadata} from './ProcessTaskStructuredTableAdapter';

type ProcessTask = ExtractByTypename<BulkFetchProcessTaskQuery['processTasks'][number], 'ProcessTaskNode'>;

export const BULK_FETCH_PROCESS_TASK_QUERY: DocumentNode = gql`
  query BulkFetchProcessTaskQuery($taskIds: [StrictString!]!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTasks(ids: $taskIds) {
      ...ProcessTaskRowGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskRow').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<ProcessTask | InaccessibleInfoGraphQL>> {
  const {processTasks} = await doQuerySafe<BulkFetchProcessTaskQuery, BulkFetchProcessTaskQueryVariables>(
    {query: BULK_FETCH_PROCESS_TASK_QUERY},
    {
      variables: {
        taskIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  return processTasks;
}

const ProcessTaskRowUpdater: StructuredTableRowUpdater<ProcessTask, ProcessTaskStructuredTableRowMetadata> = {
  fetchModel: (sampleLookupInfo: SampleLookupInfo): Promise<ProcessTask | InaccessibleInfoGraphQL> =>
    _bulkFetchModel([sampleLookupInfo]).then((processTasks) => processTasks[0]),

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<ProcessTask | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell: (
    task: ProcessTask | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    if (isInaccessible(task)) {
      return 'NO_VALUE';
    }
    switch (column.columnType) {
      case TableColumnType.PROCESS_TASK:
        // Tasks are rendered with their humanId, which is not subject to staleness
        return 'NO_VALUE';

      case TableColumnType.PROCESS_TASK_STATUS:
        return makeTaskStatusCell(task).typedValue;

      case TableColumnType.PROCESS_TASK_ASSIGNEE:
        return makeTaskAssigneeCell(task).typedValue;

      case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
        return makeScheduledOnCell(task).typedValue;

      default: {
        const matchingTag = Array.isArray(task.fields)
          ? task.fields.find((tag: SchemaTag) => isColumnForSchemaField(column, tag.tagSchemaField))
          : null;
        if (matchingTag) {
          return convertTagToTypedValue(matchingTag);
        }

        return 'NO_VALUE';
      }
    }
  },

  updateRow(
    task: ProcessTask | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcessTaskStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ProcessTaskStructuredTableRowMetadata> {
    if (isInaccessible(task)) {
      return row;
    }

    const cells = row.cells.map((cell, columnIndex) => {
      const liveValue = this.updateCell(task, columnGroup, columns[columnIndex]);
      return updateCellWithLiveValue(cell, liveValue, {setCellBlankIfLiveValueNull: true});
    });

    return {
      ...row,
      cells,
      lastSyncedArchivePurpose: task.archiveRecord?.purpose ?? null,
    };
  },

  readFromCache: ({idForLookup: apiIdentifier}: SampleLookupInfo): ProcessTask | null =>
    readFragmentFromCache(getFragmentWrapper('ProcessTaskRow'), apiIdentifier),
};

export function addToProcessTaskRowUpdaterCache(tasks: Array<ProcessTask>): void {
  const apolloClient = getApolloClient();
  apolloClient.writeQuery({
    query: BULK_FETCH_PROCESS_TASK_QUERY,
    data: {processTasks: tasks},
    variables: {taskIds: tasks.map((task) => task.id)},
  });
}

export default ProcessTaskRowUpdater;
